import { useState, useEffect, useContext, useRef, useCallback } from "react";
import {
  getVersions,
  getRnCVersions,
  getOGJobDescription,
  getSBJobDescription,
  getCompareData,
  getAutomationPotential,
  publishJD,
  unPublishJD,
  editJD,
  regenJD
} from "Api/apiService";
import { userHasEntitlement } from "Utils/parseEntitlements";
import { AuthContext } from "State/AuthContext";
import useJobsStore from "Store/useJobsStore";
import useEntitlementsStore from "Store/useEntitlementsStore";

const useJobDetails = () => {
  const selectedJob = useJobsStore((state) => state.selectedJob);
  const { email, accessToken } = useContext(AuthContext);
  const { entitlementsData, profileRole } = useEntitlementsStore();
  const cachedData = useRef({});

  // Core state
  const [versionData, setVersionData] = useState([]);
  const [selectedJdVersion, setSelectedJdVersion] = useState(1.0);
  const [sbUniqueId, setSbUniqueId] = useState('');

  // --- NEW WORKFLOW STATES ---
  const [mongoWorkflowStatus, setMongoWorkflowStatus] = useState("");
  const [reviewHistory, setReviewHistory] = useState([]);

  // Job descriptions and evaluations
  const [ogJd, setOgJd] = useState([]);
  const [sbJd, setSbJd] = useState("");
  const [editableMarkdown, setEditableMarkdown] = useState("");
  const [ogJdEval, setOgJdEval] = useState([]);
  const [sbJdEval, setSbJdEval] = useState([]);
  const [meanOgJdEval, setMeanOgJdEval] = useState([]);
  const [meanSbJdEval, setMeanSbJdEval] = useState([]);

  // Comparison and automation data
  const [comparisonSummary, setComparisonSummary] = useState([]);
  const [skillsData, setSkillsData] = useState([]);
  const [overallPotential, setOverallPotential] = useState(null);

  // UI state
  const [loading, setLoading] = useState(false);
  const [loadingAP, setLoadingAP] = useState(false);
  const [error, setError] = useState(null);
  const [blurredJD, setBlurredJD] = useState(false);
  const [jdPublished, setJdPublished] = useState(false);
  const [humanEditedJd, sethumanEditedJd] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [sbJdStatus, setSBJdStatus] = useState("");

  // Files and ownership
  const [publishedJDfiles, setPublishedJDfiles] = useState({ pdfUrl: "", docxUrl: "" });
  const [currentOwner, setCurrentOwner] = useState('');
  const [assignedApproverName, setAssignedApproverName] = useState('');
  const [updatedJD, setUpdatedJD] = useState(null);

  // JD Owner
  const [rootOwnerEmail, setRootOwnerEmail] = useState("");

  // Loading states
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [sbJdLoading, setSbJdLoading] = useState(false);
  const [sbJdEvalLoading, setSbJdEvalLoading] = useState(false);
  const [ogJdLoading, setOgJdLoading] = useState(false);
  const [ogJdEvalLoading, setOgJdEvalLoading] = useState(false);
  const [compareSumLoading, setCompareSumLoading] = useState(false);

  // Helper functions
  const resetJdData = useCallback(() => {
    setSbJd("");
    setSbJdEval("");
    setEditableMarkdown("");
    setOgJd("");
    setOgJdEval("");
    setMongoWorkflowStatus("");
    setReviewHistory([]);
    setRootOwnerEmail(""); 
  }, []);

  const getIdToUse = useCallback((fallbackId) => {
    return sbUniqueId || fallbackId;
  }, [sbUniqueId]);

  const handleError = useCallback((error, message) => {
    console.error(message, error);
    setError(`${message}. Please try again.`);
  }, []);

  const processPublishedFiles = useCallback((documents) => {
    if (!Array.isArray(documents)) return;

    const files = { pdfUrl: "", docxUrl: "" };
    documents.forEach((file) => {
      if (file.fileType === "pdf") {
        files.pdfUrl = file.signedUrl;
      } else if (file.fileType === "docx") {
        files.docxUrl = file.signedUrl;
      }
    });
    setPublishedJDfiles(files);
  }, []);

  const fetchOGJobDescription = useCallback(async () => {
    if (!selectedJob?.job_id) return;

    setOgJdLoading(true);
    setError(null);

    try {
      const response = await getOGJobDescription(selectedJob.job_id, email, accessToken);
      if (response?.base_job_description) {
        setOgJd(response.base_job_description);
      } else {
        setError('No original JD found or invalid response.');
      }
    } catch (err) {
      handleError(err, 'Failed to fetch original JD');
    } finally {
      setOgJdLoading(false);
    }
  }, [selectedJob, email, accessToken, handleError]);

  const fetchComparisonSummary = useCallback(async (targetId) => {
    setCompareSumLoading(true);
    setError(null);

    try {
      const response = await getCompareData(targetId, selectedJob.job_id, email, accessToken);
      const comparisonData = response?.compare_data?.difference_table?.jobDescriptionComparison;

      if (comparisonData) {
        setComparisonSummary(comparisonData);
      } else {
        setError('No comparison data found.');
      }
    } catch (err) {
      handleError(err, 'Error fetching comparison summary');
    } finally {
      setCompareSumLoading(false);
    }
  }, [email, accessToken, handleError]);

  const fetchTaskAutomationData = useCallback(async (targetId) => {
    setLoadingAP(true);

    // Check cache first
    if (cachedData.current[targetId]) {
      const { skillsData: cachedSkills, overallPotential: cachedPotential } = cachedData.current[targetId];
      setSkillsData(cachedSkills);
      setOverallPotential(cachedPotential);
      setLoadingAP(false);
      return;
    }

    try {
      const response = await getAutomationPotential(targetId, email, accessToken);
      if (response?.automation_potential) {
        const { automation_potential } = response.automation_potential;
        const overall = parseInt(response.automation_potential.overall_role_automation_potential);

        // Cache the response
        cachedData.current[targetId] = {
          skillsData: automation_potential,
          overallPotential: overall,
        };

        setSkillsData(automation_potential);
        setOverallPotential(overall);
      }
    } catch (error) {
      console.error("Error fetching automation potential data:", error);
    } finally {
      setLoadingAP(false);
    }
  }, [email, accessToken]);

  const fetchSBJobDescription = useCallback(async (targetSbUniqueId) => {
    const idToUse = targetSbUniqueId || selectedJob?.sb_unique_id;
    if (!idToUse) return;

    setLoading(true);
    setError(null);
    resetJdData();

    try {
      const response = await getSBJobDescription(idToUse, email, accessToken);

      if (!response) {
        setError('No JD found or invalid response.');
        return;
      }
      if (response.assigned_approver_name){
        setAssignedApproverName(response.assigned_approver_name);
      }

      // Capture the Root Owner Email
      if (response.owner_email !== undefined) {
        setRootOwnerEmail(response.owner_email);
      }

      // CAPTURE MONGODB WORKFLOW METADATA
      if (response.status) {
        setMongoWorkflowStatus(response.status);
      }
      if (response.review_history) {
        setReviewHistory(response.review_history);
      }

      // Process documents
      if (response.documents) {
        processPublishedFiles(response.documents);
      }

      // Set job description
      if (response.job_description) {
        setSbJd(response.job_description);
        setEditableMarkdown(response.job_description);
      }

      // Set current owner
      if (response.current_owner !== null) {
        setCurrentOwner(response.current_owner);
      }

      // Handle preview mode
      if (response.is_preview && response.message) {
        setBlurredJD(true);
      }

      // Handle different JD statuses
      const { jd_status, jd_input_type } = response;
      setSBJdStatus(jd_status)
      sethumanEditedJd(jd_status === "HUMAN-EDITED");

      // Fetch additional data based on status and input type
      if (jd_status === "GENERATED") {
        if (jd_input_type !== "create") {
          await fetchOGJobDescription();
          await fetchComparisonSummary(idToUse);
        }
        await fetchTaskAutomationData(idToUse);
      } else if (jd_status === "HUMAN-EDITED") {
        if (jd_input_type !== "create") {
          await fetchOGJobDescription();
        }
        if (jd_input_type === "regenerate") {
          sethumanEditedJd(false);
          await fetchTaskAutomationData(idToUse);
          await fetchComparisonSummary(idToUse);
        }
      } else {
        // Other statuses
        sethumanEditedJd(false);
        await fetchOGJobDescription();
        await fetchTaskAutomationData(idToUse);
        await fetchComparisonSummary(idToUse);
      }
    } catch (err) {
      handleError(err, 'Failed to fetch JD');
    } finally {
      setLoading(false);
    }
  }, [
    selectedJob,
    email,
    accessToken,
    resetJdData,
    processPublishedFiles,
    fetchOGJobDescription,
    fetchComparisonSummary,
    fetchTaskAutomationData,
    handleError
  ]);

  const fetchVersions = useCallback(async () => {
    if (!selectedJob?.job_id) return;
    setVersionsLoading(true);
    setError(null);

    try {
      const useRnC = userHasEntitlement(entitlementsData, profileRole, "jobs_library-version", "audit");
      const response = useRnC
        ? await getRnCVersions(email, selectedJob.job_id, accessToken)
        : await getVersions(email, selectedJob.job_id, accessToken);

      if (response && response.length > 0) {
        setVersionData(response);

        const trueLatestVersion = response[0];
        const currentIdInUse = getIdToUse(selectedJob.sb_unique_id);

        if (trueLatestVersion.sb_unique_id !== currentIdInUse) {
          console.log("Newer version found. Auto-switching to version:", trueLatestVersion.version);

          setSbUniqueId(trueLatestVersion.sb_unique_id);
          setSelectedJdVersion(trueLatestVersion.version);

          await fetchSBJobDescription(trueLatestVersion.sb_unique_id);
        } else {
          setSelectedJdVersion(trueLatestVersion.version);
        }
      } else {
        setError('No versions found or invalid response.');
      }
    } catch (err) {
      handleError(err, 'Failed to fetch versions');
    } finally {
      setVersionsLoading(false);
    }
  }, [email, selectedJob, accessToken, getIdToUse, handleError, entitlementsData, profileRole, fetchSBJobDescription]);

  const refreshData = useCallback(async (targetSbUniqueId) => {
    await fetchVersions();
    await fetchSBJobDescription(targetSbUniqueId);
  }, [fetchVersions, fetchSBJobDescription]);

  // Action functions
  const publishJd = useCallback(async () => {
    const idToUse = getIdToUse(selectedJob?.sb_unique_id);
    if (!idToUse) return;

    setLoading(true);
    setError(null);

    try {
      const response = await publishJD(idToUse, email);

      if (response.success && response.status === "PUBLISHED" && response.sb_unique_id) {
        setJdPublished(true);
        setSbUniqueId(response.sb_unique_id);
        resetJdData();

        await fetchSBJobDescription(response.sb_unique_id);
        await fetchVersions(response.sb_unique_id);
        setSelectedJdVersion(response.version);
      } else if (
        response.success &&
        response.status === "UNPUBLISHED" &&
        response.message === "No new changes to publish. Edit to continue"
      ) {
        setJdPublished(false);
        setToastMessage("Make changes before republishing.");
        setShowToast(true);
      } else {
        setError('Invalid response.');
      }
    } catch (err) {
      handleError(err, 'Failed to publish JD');
    } finally {
      setLoading(false);
    }
  }, [
    getIdToUse,
    selectedJob,
    email,
    accessToken,
    resetJdData,
    fetchSBJobDescription,
    fetchVersions,
    handleError
  ]);

  const unPublishJd = useCallback(async () => {
    const idToUse = getIdToUse(selectedJob?.sb_unique_id);
    if (!idToUse) return;

    setLoading(true);
    setError(null);

    try {
      const response = await unPublishJD(idToUse, email, accessToken);
      console.log(response)
      if (response.success && response.status !== "PUBLISHED") {
        setJdPublished(false);
        setCurrentOwner(response.current_owner);

        await refreshData(idToUse);

      } else {
        setError('Invalid response.');
      }
    } catch (err) {
      handleError(err, 'Failed to unpublish JD');
    } finally {
      setLoading(false);
    }
  }, [getIdToUse, selectedJob, email, accessToken, handleError, refreshData]);

  const saveEditedJd = useCallback(async (updatedJdCurrent) => {
    const idToUse = getIdToUse(selectedJob?.sb_unique_id);
    if (!idToUse || !selectedJob?.job_code) return;

    setLoading(true);
    setError(null);

    try {
      const response = await editJD(idToUse, email, updatedJdCurrent, selectedJob.job_code);

      if (response.success && response.sb_unique_id) {
        resetJdData();

        await fetchSBJobDescription(response.sb_unique_id);
        await fetchVersions(response.sb_unique_id);

        setSbUniqueId(response.sb_unique_id);
        setSelectedJdVersion(response.version);

        return response;
      } else {
        setError('Invalid response.');
      }
    } catch (err) {
      handleError(err, 'Failed to save edited JD');
    } finally {
      setLoading(false);
    }
  }, [
    getIdToUse,
    selectedJob,
    email,
    resetJdData,
    fetchSBJobDescription,
    fetchVersions,
    handleError
  ]);

  const regenerateJd = useCallback(async (humanMessage) => {
    const idToUse = getIdToUse(selectedJob?.sb_unique_id);
    if (!idToUse) return;

    setLoading(true);
    setError(null);

    try {
      const response = await regenJD(idToUse, email, humanMessage, editableMarkdown, accessToken);

      if (response?.success && response.sb_unique_id) {
        setSbUniqueId(response.sb_unique_id);
        resetJdData();

        await fetchSBJobDescription(response.sb_unique_id);
        await fetchVersions(response.sb_unique_id);

        setSelectedJdVersion(response.version);
      } else if (
        response?.success &&
        response.status === "UNPUBLISHED" &&
        response.message === "No new changes to regenerate. Edit to continue."
      ) {
        setToastMessage("No new changes to publish. Edit JD to continue");
        setShowToast(true);
      } else {
        setError('Invalid response.');
      }
    } catch (err) {
      handleError(err, 'Failed to regenerate JD');
    } finally {
      setLoading(false);
    }
  }, [
    getIdToUse,
    selectedJob,
    email,
    editableMarkdown,
    accessToken,
    resetJdData,
    fetchSBJobDescription,
    fetchVersions,
    handleError
  ]);

  const handleSbUniqueIdChange = useCallback((newSbUniqueId) => {
    resetJdData();
    fetchSBJobDescription(newSbUniqueId);
  }, [resetJdData, fetchSBJobDescription]);

  // Effects
  useEffect(() => {
    if (selectedJob?.jd_status) {
      setJdPublished(selectedJob.jd_status === 'PUBLISHED');
    }
  }, [selectedJob]);

  useEffect(() => {
    const sbUniqueIdToUse = selectedJob?.sb_unique_id;
    if (!sbUniqueIdToUse) return;
    fetchSBJobDescription();
    fetchVersions();
  }, [selectedJob?.sb_unique_id]);


  return {
    // Data
    versionData,
    selectedJdVersion,
    setSelectedJdVersion,
    ogJd,
    sbJd,
    mongoWorkflowStatus,
    reviewHistory,
    sbUniqueId,
    setSbUniqueId,
    ogJdEval,
    sbJdEval,
    updatedJD,
    setUpdatedJD,
    editableMarkdown,
    setEditableMarkdown,
    meanOgJdEval,
    meanSbJdEval,
    comparisonSummary,
    skillsData,
    overallPotential,
    publishedJDfiles,
    currentOwner,
    setCurrentOwner,
    assignedApproverName,
    setAssignedApproverName,

    // UI State
    loading,
    versionsLoading,
    sbJdLoading,
    sbJdEvalLoading,
    ogJdLoading,
    ogJdEvalLoading,
    compareSumLoading,
    loadingAP,
    error,
    blurredJD,
    jdPublished,
    toastMessage,
    setToastMessage,
    showToast,
    setShowToast,
    humanEditedJd,
    sethumanEditedJd,
    sbJdStatus,

    // Actions
    handleSbUniqueIdChange,
    publishJd,
    unPublishJd,
    saveEditedJd,
    regenerateJd,
    refreshData,

    // JD Owner
    rootOwnerEmail,
    setRootOwnerEmail,
  };
};

export default useJobDetails;